// @ts-strict-ignore
import Button from 'components/Button'

import Condition from 'components/Condition'
import VideoRecorder from 'components/VideoRecorder'
import { IUploadedVideo, useVideoRecorder } from 'hooks/video'
import { useFetchKey } from 'hooks/relay'
import React, { useCallback, useState, useEffect, CSSProperties } from 'react'
import { VideoCall } from '@mui/icons-material'
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay'
import { RecordCompanyVideoButton_Firm$key } from '__generated__/RecordCompanyVideoButton_Firm.graphql'
import { RecordCompanyVideoButton_Query } from '__generated__/RecordCompanyVideoButton_Query.graphql'
import { IconButton } from '@mui/material'

interface IRecordVideoButtonProps {
  firmId: string
  onVideoRecorded: (video: IUploadedVideo) => void
  style?: CSSProperties
  iconOnly?: boolean
}

const RecordCompanyVideoButton = ({ firmId, onVideoRecorded, style = {}, iconOnly }: IRecordVideoButtonProps) => {
  const [fetchKey, increaseFetchKey] = useFetchKey()

  const data = useLazyLoadQuery<RecordCompanyVideoButton_Query>(graphql`
    query RecordCompanyVideoButton_Query($firmId: ID!) {
      firm: node(id: $firmId) {
        ...RecordCompanyVideoButton_Firm
      }

      configuration {
        videoMaxDurationCompany
        videoMinDuration
      }
    }
  `, { firmId }, {
    fetchPolicy: 'store-and-network',
    fetchKey,
  })

  const [hasVideo, setHasVideo] = useState(false)

  const firm = useFragment(graphql`
    fragment RecordCompanyVideoButton_Firm on Firm {
      rawId
      self
      video {
        rawId
      }
    }
  `, data?.firm as RecordCompanyVideoButton_Firm$key)

  useEffect(() => { if (firm?.video) setHasVideo(true) }, [firm?.video])

  const onUploadCompleted = useCallback((video: IUploadedVideo) => {
    setHasVideo(true)
    onVideoRecorded(video)

    if (firm?.self) {
      increaseFetchKey()
    }
  }, [onVideoRecorded, firm?.self, increaseFetchKey])

  const videoRecording = useVideoRecorder({
    videoParams: {
      firmRawId: firm?.rawId,
    },
    onUploadCompleted: onUploadCompleted,
  })

  if (!firm?.self) return null

  return (
    <React.Fragment>
      <Condition condition={!iconOnly}>
        <Button color="primary" onClick={videoRecording.openVideoRecorder} disabled={videoRecording.isUploading} style={style} startIcon={<VideoCall />}>
          {videoRecording.isUploading && 'Uploading...'}
          {!videoRecording.isUploading && (hasVideo ? 'Replace Video' : 'Add Video')}
        </Button>
      </Condition>

      <Condition condition={iconOnly}>
        <IconButton onClick={videoRecording.openVideoRecorder} disabled={videoRecording.isUploading} style={style}>
          <VideoCall />
        </IconButton>
      </Condition>

      {videoRecording.isVideoRecorderOpen && (
        <VideoRecorder
          title="Company Video"
          maxDuration={data?.configuration?.videoMaxDurationCompany}
          minDuration={data?.configuration?.videoMinDuration}
          {...videoRecording.videoRecorderProps}
        />
      )}
    </React.Fragment>
  )
}

export default RecordCompanyVideoButton
